import {MetricEntry} from 'common/query-builder/query-entry-section/types';
import {reporter} from 'common/report/error-reporting';
import {TopLevelMetricType} from 'common/types/reports/bookmark';
import {getAlphaLabelFromIndex} from 'common/util';
import * as BookmarkTypes from 'common/types/reports/bookmark';

import {ShowClause} from '../util/types';
import formulaParser from './formula-parser-compiled';

const {get_parser: getFormulaParser, Token, UnexpectedCharacters, UnexpectedToken} = formulaParser;

/**
 * Updates a formula definition by replacing old labels with new labels.
 *
 * @param referenceMap - Mapping of old labels to new labels
 */
export function replaceFormulaReferences(formula: string, referenceMap: Record<string, string>) {
  return formula.replace(/\w+/g, (sub) => {
    // if the label is in the reference map, replace it with the new label. otherwise we'll leave it alone
    return referenceMap[sub] || sub;
  });
}

/**
 * For a formula and all the metrics it references, we need to map
 * existing positions in the report entries to what they would become
 * inside a saved formula referenced metrics array.
 *
 * For example, if a report has 3 metrics and an inline formula only references metric `C`,
 * when saving this formula, the variable `C` will now become `A` and this metric will
 * be index 0 in the `referencedMetrics` field of the saved formula
 */
export function getUpdatedFormulaAndMetrics({
  formula,
  clauses,
  validate = false,
}: {
  formula: BookmarkTypes.FormulaShowClause;
  clauses: ShowClause[];
  validate?: boolean;
}): {definition: string; referencedMetrics: Array<BookmarkTypes.BehaviorShowClause>; isValid: boolean} {
  const formulaOperands = new Set(formula.definition!.match(/[A-Z]{1,}/g));

  // a map of the metrics referenced by the inline formula
  // to the variable identifier they are currently using
  const referencedMetrics: Array<BookmarkTypes.BehaviorShowClause> = [];
  const updatedFormulaMap: Record<string, string> = {};
  let isValid = true;

  for (const [index, clause] of clauses.entries()) {
    const identifier = getAlphaLabelFromIndex(index);

    if (`behavior` in clause === false) {
      if (formulaOperands.has(identifier)) {
        isValid = false;
        if (validate) {
          throw new Error(`Attempted to save a formula within a formula`);
        }
      }
      continue;
    }

    if (formulaOperands.has(identifier)) {
      const newIdentifier = getAlphaLabelFromIndex(referencedMetrics.length);
      referencedMetrics.push(clause);
      updatedFormulaMap[identifier] = newIdentifier;
    }
  }

  // saving a constant formula
  if (referencedMetrics.length === 0) {
    return {
      isValid,
      definition: formula.definition!,
      referencedMetrics: [],
    };
  }

  return {
    isValid,
    referencedMetrics,
    definition: replaceFormulaReferences(formula.definition!, updatedFormulaMap),
  };
}

/**
 * Validates a formula metric definition and returns an error message if the formula is invalid.
 *
 * @param {Object} params - The parameters for the validation function.
 * @param {BookmarkTypes.FormulaShowClause} params.metric - The formula metric being validated.
 * @param {string} [params.definition] - An updated definition to validate against. If not provided, the metric's definition will be used.
 * @param {BookmarkTypes.InsightsBookmarkParams['sections']['show']} params.globalMetrics - List of all the available metrics in the Query Builder.
 *
 * @returns {string | null} - Returns an error message if the formula is invalid, otherwise returns null.
 */
export function getFormulaValidationError({
  metric,
  definition: updatedDefinition,
  globalMetrics,
}: {
  /** the formula metric being validated */
  metric: MetricEntry;
  /** an updated definition to validation on */
  definition?: string;
  /** list of all the available metrics in the QB */
  globalMetrics: MetricEntry[];
}): string | null {
  const definition = updatedDefinition ?? metric.definition;

  if (definition) {
    const entries = metric.referencedMetrics?.length ? metric.referencedMetrics : globalMetrics;
    const labelsToEntries = Object.fromEntries(entries.map((entry, idx) => [getAlphaLabelFromIndex(idx), entry]));
    const validLabels = Object.entries(labelsToEntries)
      .filter(([_, entry]) => entry.type !== TopLevelMetricType.NewFormulaEntry)
      .map(([label]) => label);

    try {
      parseFormula(definition, validLabels);
    } catch (err) {
      if (err instanceof FormulaParseError) {
        if (err instanceof FormulaParseVariableError) {
          const entry = labelsToEntries[err.variable];

          if (entry && entry.type === TopLevelMetricType.NewFormulaEntry) {
            return `Cannot reference formulas in formulas: ${err.variable}`;
          }
        }

        return err.message;
      }

      // we can ignore non-parser errors as to not interrupt the UX. the formula will be validated again at query
      reporter.error(err);
    }
  }

  return null;
}

export class FormulaParseError extends Error {}
export class FormulaParseVariableError extends FormulaParseError {
  constructor(public variable: string) {
    super(`Unknown variable: ${variable}`);
  }
}

let _parser;

/**
 * Parses a formula and returns the AST.
 */
export function parseFormula(formula: string, availableVariables: string[]) {
  try {
    _parser ||= getFormulaParser();

    const tree = _parser.parse(formula);

    // ensure only variables that are available are used
    for (const token of tree.scan_values((node: {type: string}) => node instanceof Token && node.type === `VAR`)) {
      if (!availableVariables.includes(token.value)) {
        throw new FormulaParseVariableError(token.value);
      }
    }

    return tree;
  } catch (err) {
    if (err instanceof UnexpectedToken) {
      if (err.token.type === `$END`) {
        throw new FormulaParseError(`Incomplete formula`);
      }

      throw new FormulaParseError(`Unexpected token: ${err.token.value}`);
    } else if (err instanceof UnexpectedCharacters) {
      throw new FormulaParseError(`Unexpected character: ${err.char}`);
    }

    throw err;
  }
}
